import {
  Component,
  Inject,
  LOCALE_ID,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { from } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { EmployeeSettings } from '../../../model/employee/employee-settings';
import { UpdateEmployeeSettings } from '../../../model/employee/request/update-employee-settings-request';
import { ProfileDataService } from '../../../services/data/profile-data.service';
import { DisplayLanguageService } from '../../../shared/services/display-language.service';
import { Language } from '../../../util/language';
import { normalizePreferredLanguage } from '../../../model/employee/employee-settings-language';

@Component({
  selector: 'app-edit-profile-preferences-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
  ],
  templateUrl: './edit-profile-preferences-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-profile-preferences-dialog.component.scss',
})
export class EditProfilePreferencesDialogComponent {
  protected readonly loading$ = signal(false);
  protected readonly defaultDisplayLanguage = 'de';
  protected readonly displayLanguageOptions = ['de', 'en'].map((code) => ({
    code,
    label: `${Language.getFlagFromLanguageCode(
      code,
    )} ${Language.getDisplayNameFromLanguageCode(code, this.locale)}`,
  }));
  protected readonly settingsForm = new FormGroup({
    displayLanguage: new FormControl('de', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EmployeeSettings,
    @Inject(LOCALE_ID) private readonly locale: string,
    private readonly dialogRef: MatDialogRef<EditProfilePreferencesDialogComponent>,
    private readonly profileDataService: ProfileDataService,
    private readonly displayLanguageService: DisplayLanguageService,
    private readonly snackBar: MatSnackBar,
  ) {
    this.settingsForm.patchValue({
      displayLanguage: data?.displayLanguage ?? this.defaultDisplayLanguage,
    });

    this.settingsForm.markAsPristine();
  }

  protected onSave(): void {
    if (this.settingsForm.invalid) {
      return;
    }

    const request: UpdateEmployeeSettings = {
      displayLanguage: this.settingsForm.controls.displayLanguage.value,
      preferredLanguage: normalizePreferredLanguage(
        this.data.preferredLanguage,
      ),
      photoCountdownSeconds: this.data.photoCountdownSeconds,
      liveImageDurationSeconds: this.data.liveImageDurationSeconds,
      favoriteSmsCountryCodes: this.data.favoriteSmsCountryCodes,
    };

    this.loading$.set(true);
    this.profileDataService
      .updateProfileSettings(request)
      .pipe(
        switchMap(() =>
          from(
            this.displayLanguageService.applyDisplayLanguage(
              this.settingsForm.controls.displayLanguage.value,
            ),
          ),
        ),
        finalize(() => this.loading$.set(false)),
      )
      .subscribe({
        next: (didTriggerReload) => {
          if (didTriggerReload) {
            return;
          }

          this.snackBar.open(
            $localize`Einstellungen wurden gespeichert.`,
            $localize`Ok`,
            {
              duration: 3_000,
            },
          );
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackBar.open(
            $localize`Einstellungen konnten nicht gespeichert werden.`,
            $localize`Ok`,
            {
              duration: 5_000,
            },
          );
        },
      });
  }
}
